import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon, MessageCircle, BedDouble, Sparkles, Plus, Compass } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import FavoriteButton from "@/components/FavoriteButton";
import PillarBadge from "@/components/PillarBadge";
import { PILLAR_DEFS, pillarFeeFor } from "@/lib/pillars";
import { money } from "@/lib/currency";

function addOnUnitLabel(unit) {
  if (unit === "per_person") return "/ guest";
  if (unit === "per_night") return "/ night";
  return "flat";
}

function computeAddOnTotal(addOn, guests, nights) {
  if (addOn.unit === "per_person") return addOn.price_usd * guests;
  if (addOn.unit === "per_night") return addOn.price_usd * nights;
  return addOn.price_usd;
}

function computeRoomTotal(room, nights) {
  if (!room) return 0;
  return room.rate_type === "flat" ? room.rate_usd : room.rate_usd * nights;
}

export default function BookingBuilder({ exp, onMessageHost }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rooms = useMemo(() => exp.rooms || [], [exp.rooms]);
  const packages = useMemo(() => exp.packages || [], [exp.packages]);
  const addOnsAll = useMemo(() => exp.add_ons || [], [exp.add_ons]);
  const hasNewModel = rooms.length > 0 || packages.length > 0;

  // Show all four pillars in the builder so travelers can opt-in to any engagement mode
  // — the host may have priced a pillar without tagging it in exp.pillars.
  const availablePillars = PILLAR_DEFS;
  const pricing = exp.pillar_pricing || null;

  const [guests, setGuests] = useState(1);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [roomId, setRoomId] = useState(rooms[0]?.id || null);
  const [packageId, setPackageId] = useState(packages[0]?.id || null);
  const [addOnIds, setAddOnIds] = useState([]);
  const [pillarKeys, setPillarKeys] = useState([]);
  const [sharingMode, setSharingMode] = useState(pricing?.sharing_mode || "volunteer");
  const [booking, setBooking] = useState(false);

  const selectedRoom = useMemo(() => rooms.find((r) => r.id === roomId), [rooms, roomId]);
  const selectedPkg = useMemo(() => packages.find((p) => p.id === packageId), [packages, packageId]);
  const nights = useMemo(() => {
    if (checkIn && checkOut) {
      const a = new Date(checkIn + "T00:00:00");
      const b = new Date(checkOut + "T00:00:00");
      const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
      if (diff > 0) return diff;
    }
    return selectedPkg?.duration_days || exp.duration_days || 1;
  }, [checkIn, checkOut, selectedPkg, exp.duration_days]);

  const roomSubtotal = useMemo(() => computeRoomTotal(selectedRoom, nights), [selectedRoom, nights]);
  const pkgSubtotal = useMemo(() => (selectedPkg ? selectedPkg.price_usd * guests : 0), [selectedPkg, guests]);
  const addOnsSelected = useMemo(() => addOnsAll.filter((a) => addOnIds.includes(a.id)), [addOnsAll, addOnIds]);
  const addOnsSubtotal = useMemo(
    () => addOnsSelected.reduce((s, a) => s + computeAddOnTotal(a, guests, nights), 0),
    [addOnsSelected, guests, nights]
  );

  const pillarBreakdown = useMemo(() => {
    return pillarKeys.map((k) => ({ key: k, fee: pillarFeeFor(k, pricing, sharingMode) }));
  }, [pillarKeys, pricing, sharingMode]);
  const pillarSubtotal = useMemo(
    () => pillarBreakdown.reduce((s, p) => s + p.fee, 0),
    [pillarBreakdown]
  );

  const grossPrice = useMemo(() => {
    if (!hasNewModel) return (exp.price_usd || 0) * guests;
    return roomSubtotal + pkgSubtotal + addOnsSubtotal + pillarSubtotal;
  }, [hasNewModel, exp.price_usd, guests, roomSubtotal, pkgSubtotal, addOnsSubtotal, pillarSubtotal]);

  // Host-configured percentage discount on the final total (0–100).
  const discountPercent = Math.max(0, Math.min(100, Number(pricing?.discount_percent || 0)));
  const discountAmount = useMemo(() => (grossPrice * discountPercent) / 100, [grossPrice, discountPercent]);
  const totalPrice = useMemo(() => Math.max(0, grossPrice - discountAmount), [grossPrice, discountAmount]);

  const toggleAddOn = (id) => setAddOnIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const togglePillar = (key) => setPillarKeys((cur) => (cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]));

  const bookNow = async () => {
    if (!user) { navigate("/login?redirect=/experience/" + exp.id); return; }
    if (!checkIn) { toast.error("Please pick a check-in date"); return; }
    if (checkOut && checkOut <= checkIn) { toast.error("Check-out must be after check-in"); return; }
    if (hasNewModel && rooms.length > 0 && !roomId) { toast.error("Please choose a room"); return; }
    if (hasNewModel && packages.length > 0 && !packageId) { toast.error("Please choose a package"); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        experience_id: exp.id,
        check_in: checkIn,
        check_out: checkOut || null,
        guests,
        origin_url: window.location.origin,
        room_id: roomId,
        package_id: packageId,
        add_on_ids: addOnIds,
        nights,
        pillars_selected: pillarKeys,
        sharing_mode: pillarKeys.includes("sharing") ? sharingMode : null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBooking(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Rooms */}
      {rooms.length > 0 && (
        <div data-testid="rooms-section">
          <div className="flex items-center gap-2 mb-4">
            <BedDouble className="h-5 w-5 text-moss" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Where you'll stay</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {rooms.map((r) => {
              const isSelected = roomId === r.id;
              return (
                <button
                  key={r.id}
                  data-testid={`room-card-${r.id}`}
                  type="button"
                  onClick={() => setRoomId(r.id)}
                  className={`text-left rounded-2xl border p-5 bg-white transition-all ${isSelected ? "border-moss ring-2 ring-moss/20" : "border-subtle hover:border-moss"}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-serif text-xl">{r.name}</div>
                    <div className="text-right">
                      <div className="font-medium">{money(r.rate_usd)}</div>
                      <div className="text-[10px] text-muted2 uppercase tracking-wide">{r.rate_type === "flat" ? "flat" : "/ night"}</div>
                    </div>
                  </div>
                  {r.description && <p className="text-xs text-muted2 mt-1">{r.description}</p>}
                  <div className="text-xs text-muted2 mt-2">Sleeps {r.capacity} · {r.quantity} available</div>
                  {r.amenities?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {r.amenities.slice(0, 5).map((a) => (
                        <span key={a} className="text-[10px] bg-subtle text-ink px-2 py-0.5 rounded-full">{a}</span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Packages */}
      {packages.length > 0 && (
        <div data-testid="packages-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-terracotta" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Choose your journey</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {packages.map((p) => {
              const isSelected = packageId === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`package-card-${p.id}`}
                  type="button"
                  onClick={() => setPackageId(p.id)}
                  className={`text-left rounded-2xl border p-5 bg-white transition-all ${isSelected ? "border-terracotta ring-2 ring-terracotta/20" : "border-subtle hover:border-terracotta"}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-serif text-xl">{p.name}</div>
                    <div className="text-right">
                      <div className="font-medium">{money(p.price_usd)}</div>
                      <div className="text-[10px] text-muted2 uppercase tracking-wide">/ guest</div>
                    </div>
                  </div>
                  {p.description && <p className="text-xs text-muted2 mt-1">{p.description}</p>}
                  <div className="text-xs text-muted2 mt-2">{p.duration_days} day{p.duration_days > 1 ? "s" : ""}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.pillars?.map((pl) => <PillarBadge key={pl} pillar={pl} />)}
                  </div>
                  {p.activities?.length > 0 && (
                    <ul className="mt-3 text-xs text-muted2 space-y-1">
                      {p.activities.slice(0, 4).map((a) => <li key={a}>· {a}</li>)}
                    </ul>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add-ons */}
      {addOnsAll.length > 0 && (
        <div data-testid="addons-section">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="h-5 w-5 text-moss" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Add to your journey</h2>
          </div>
          <div className="space-y-2">
            {addOnsAll.map((a) => {
              const checked = addOnIds.includes(a.id);
              const unitLabel = addOnUnitLabel(a.unit);
              return (
                <label
                  key={a.id}
                  data-testid={`addon-row-${a.id}`}
                  className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors bg-white ${checked ? "border-moss bg-moss/5" : "border-subtle hover:border-moss"}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAddOn(a.id)}
                    className="mt-1 accent-moss"
                    data-testid={`addon-check-${a.id}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-medium">{a.name}</div>
                      <div className="text-sm"><span className="font-medium">{money(a.price_usd)}</span> <span className="text-muted2 text-xs">{unitLabel}</span></div>
                    </div>
                    {a.description && <p className="text-xs text-muted2 mt-0.5">{a.description}</p>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom Pillar Builder */}
      {availablePillars.length > 0 && (
        <div data-testid="pillar-builder-section">
          <div className="flex items-center gap-2 mb-2">
            <Compass className="h-5 w-5 text-moss" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Design your journey</h2>
          </div>
          <p className="text-xs text-muted2 mb-4">
            Choose which pillars you&apos;d like to weave in. <span className="text-moss">Working</span> is always free — a labour exchange with the host. <span className="text-moss">Sharing</span> is a volunteer offering unless you opt into a paid masterclass.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {availablePillars.map((def) => {
              const checked = pillarKeys.includes(def.key);
              const fee = pillarFeeFor(def.key, pricing, sharingMode);
              return (
                <label
                  key={def.key}
                  data-testid={`pillar-row-${def.key}`}
                  className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors bg-white ${checked ? "border-moss bg-moss/5" : "border-subtle hover:border-moss"}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePillar(def.key)}
                    className="mt-1 accent-moss"
                    data-testid={`pillar-check-${def.key}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-medium">{def.label}</div>
                      <div className="text-sm">
                        {fee > 0 ? (
                          <>
                            <span className="font-medium" data-testid={`pillar-fee-${def.key}`}>{money(fee)}</span>
                            <span className="text-muted2 text-xs"> · one-time</span>
                          </>
                        ) : (
                          <span className="text-moss text-xs font-medium" data-testid={`pillar-fee-${def.key}`}>Free</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted2 mt-0.5">{def.description}</p>
                    {def.key === "sharing" && checked && (
                      <div className="mt-3 flex gap-2" data-testid="sharing-mode-toggle">
                        <button
                          type="button"
                          data-testid="sharing-mode-volunteer"
                          onClick={(e) => { e.preventDefault(); setSharingMode("volunteer"); }}
                          className={`flex-1 rounded-full px-3 py-1 text-[11px] border transition-colors ${
                            sharingMode === "volunteer" ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                          }`}
                        >Volunteer · Free</button>
                        <button
                          type="button"
                          data-testid="sharing-mode-masterclass"
                          onClick={(e) => { e.preventDefault(); setSharingMode("masterclass"); }}
                          disabled={!(pricing?.sharing_masterclass_fee > 0)}
                          className={`flex-1 rounded-full px-3 py-1 text-[11px] border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            sharingMode === "masterclass" ? "bg-terracotta text-canvas border-terracotta" : "bg-white border-subtle hover:border-terracotta"
                          }`}
                        >Masterclass{pricing?.sharing_masterclass_fee > 0 ? ` · ${money(pricing.sharing_masterclass_fee)}` : ""}</button>
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Booking summary card */}
      <div className="sticky top-24 bg-white border border-subtle rounded-2xl p-6 shadow-sm" data-testid="booking-card">
        <div className="flex items-baseline justify-between mb-4">
          <span className="font-serif text-2xl">Your journey</span>
          <span className="text-xs text-muted2">{nights} night{nights !== 1 ? "s" : ""}</span>
        </div>

        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1.5 block">Check-in</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="checkin-date-input" className="w-full flex items-center gap-2 rounded-xl bg-white border border-subtle px-3 py-2 text-left text-sm hover:border-moss">
                    <CalendarIcon className="h-4 w-4 text-muted2" />
                    {checkIn || <span className="text-muted2">Pick date</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={checkIn ? new Date(checkIn + "T00:00:00") : undefined}
                    onSelect={(d) => {
                      if (!d) return;
                      const iso = d.toISOString().split("T")[0];
                      setCheckIn(iso);
                      if (checkOut && checkOut <= iso) setCheckOut("");
                    }}
                    disabled={(date) => {
                      if (date < new Date(new Date().toDateString())) return true;
                      const iso = date.toISOString().split("T")[0];
                      const m = exp.availability_mode || "block";
                      if (m === "open") {
                        const w = exp.available_windows || [];
                        return !w.some((r) => r.start <= iso && iso <= r.end);
                      }
                      return (exp.unavailable_dates || []).includes(iso);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Check-out</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="checkout-date-input" className="w-full flex items-center gap-2 rounded-xl bg-white border border-subtle px-3 py-2 text-left text-sm hover:border-moss">
                    <CalendarIcon className="h-4 w-4 text-muted2" />
                    {checkOut || <span className="text-muted2">Optional</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={checkOut ? new Date(checkOut + "T00:00:00") : undefined}
                    onSelect={(d) => d && setCheckOut(d.toISOString().split("T")[0])}
                    disabled={(date) => {
                      const iso = date.toISOString().split("T")[0];
                      if (checkIn && iso <= checkIn) return true;
                      if (date < new Date(new Date().toDateString())) return true;
                      const m = exp.availability_mode || "block";
                      if (m === "open") {
                        const w = exp.available_windows || [];
                        return !w.some((r) => r.start <= iso && iso <= r.end);
                      }
                      return (exp.unavailable_dates || []).includes(iso);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {checkOut && checkIn && (
            <div className="text-[11px] text-muted2" data-testid="stay-length">
              {nights} night{nights === 1 ? "" : "s"} — long stays are billed per night on the room + any per-night add-ons
            </div>
          )}
          <div>
            <Label className="text-xs mb-1.5 block">Guests</Label>
            <Input
              data-testid="guests-input"
              type="number"
              min="1"
              max={exp.max_guests}
              value={guests}
              onChange={(e) => setGuests(Math.max(1, Math.min(exp.max_guests, parseInt(e.target.value) || 1)))}
              className="rounded-xl bg-white border-subtle"
            />
          </div>
        </div>

        {/* Line items */}
        {hasNewModel && (
          <div className="border-t border-subtle pt-4 space-y-1.5 text-sm">
            {selectedRoom && (
              <div className="flex justify-between text-muted2" data-testid="line-room">
                <span>{selectedRoom.name} {selectedRoom.rate_type === "per_night" ? `· ${nights} night${nights !== 1 ? "s" : ""}` : "· flat"}</span>
                <span>{money(roomSubtotal)}</span>
              </div>
            )}
            {selectedPkg && (
              <div className="flex justify-between text-muted2" data-testid="line-package">
                <span>{selectedPkg.name} × {guests}</span>
                <span>{money(pkgSubtotal)}</span>
              </div>
            )}
            {addOnsSelected.map((a) => (
              <div key={a.id} className="flex justify-between text-muted2" data-testid={`line-addon-${a.id}`}>
                <span>{a.name}</span>
                <span>{money(computeAddOnTotal(a, guests, nights))}</span>
              </div>
            ))}
            {pillarBreakdown.map((p) => (
              <div key={p.key} className="flex justify-between text-muted2" data-testid={`line-pillar-${p.key}`}>
                <span>
                  {PILLAR_DEFS.find(d => d.key === p.key)?.label}
                  {p.key === "sharing" ? ` · ${sharingMode === "masterclass" ? "masterclass" : "volunteer"}` : ""}
                </span>
                <span>{p.fee > 0 ? money(p.fee) : "Free"}</span>
              </div>
            ))}
            {discountPercent > 0 && (
              <>
                <div className="flex justify-between text-muted2 pt-1 border-t border-subtle" data-testid="line-subtotal">
                  <span>Subtotal</span>
                  <span>{money(grossPrice)}</span>
                </div>
                <div className="flex justify-between text-moss font-medium" data-testid="line-discount">
                  <span>Host discount ({discountPercent}%)</span>
                  <span>− {money(discountAmount)}</span>
                </div>
              </>
            )}
          </div>
        )}

        <div className="border-t border-subtle mt-4 pt-4 flex justify-between items-baseline">
          <span className="text-sm">Total</span>
          <span className="font-serif text-2xl" data-testid="booking-total">{money(totalPrice)}</span>
        </div>

        <Button
          data-testid="book-now-button"
          onClick={bookNow}
          disabled={booking}
          className="w-full mt-4 rounded-full bg-moss hover:bg-moss-hover text-canvas py-6"
        >
          {booking ? "Preparing…" : "Book this journey"}
        </Button>
        <button
          data-testid="message-host-button"
          onClick={onMessageHost}
          className="w-full mt-2 rounded-full bg-white border border-subtle hover:border-moss py-3 text-sm inline-flex items-center justify-center gap-2 transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Message the host
        </button>
        <div className="mt-4 flex items-center justify-center">
          <FavoriteButton expId={exp.id} initialCount={exp.favorite_count || 0} />
        </div>
        <p className="text-xs text-muted2 mt-3 text-center">You'll be redirected to secure checkout.</p>
      </div>
    </div>
  );
}
